import { Sparkles, Play, Calendar, MessageCircle, TrendingUp, Zap } from "lucide-react";
import { useTheme } from '@/contexts/ThemeContext';

const EditorialPlatformSectionAlt = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const badges = [
    { icon: Play, label: "Create" },
    { icon: Calendar, label: "Schedule" },
    { icon: MessageCircle, label: "Engage" },
    { icon: TrendingUp, label: "Analyze" },
    { icon: Zap, label: "Automate" },
  ];

  return (
    <section 
      className="w-full py-16 px-[7%] sm:px-6"
      style={{ backgroundColor: isDark ? '#111111' : '#FFFFFF' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
          {/* Left Content */}
          <div className="lg:flex-1">
            {/* Platform Badge - with animated border */}
            <div className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full overflow-hidden mb-6">
              {/* Animated border - rotating gradient */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: isDark 
                    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
                    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'badge-border-trace 2s linear infinite',
                }}
              />
              
              {/* Static border underneath */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                }}
              />
              
              {/* Inner background */}
              <div 
                className="absolute inset-[1px] rounded-full"
                style={{
                  backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                }}
              />
              
              <Sparkles className="relative z-10 w-3 h-3" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
              <span className="relative z-10 text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Platform</span>
            </div>
            

            {/* Heading */}
            <h2 
              className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              How Businesses Are Using Storily.
            </h2>

            {/* Subtitle */}
            <p 
              className="text-sm mb-6 max-w-2xl"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              One platform to create, schedule, engage, analyze, and automate. Replace 10 tools with one.
            </p>

            {/* Feature Badges */}
            <div className="flex flex-wrap gap-2">
              {badges.map((badge) => (
                <div
                  key={badge.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-200 hover:scale-105 hover:shadow-lg cursor-pointer"
                  style={{
                    backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                    border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                  }}
                >
                  <badge.icon className="w-3 h-3" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                  <span className="text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Bento Cards - Side by side portrait */}
          <div className="flex flex-row gap-3 lg:mt-6">
            {/* Hours Saved Card */}
            <div 
              className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8',
                border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                boxShadow: isDark 
                  ? '0 15px 30px -8px rgba(0, 0, 0, 0.4)' 
                  : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)'
              }}
            >
              <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>130+</div>
              <div className="text-xs" style={{ color: isDark ? '#AAAAAA' : '#555555' }}>hours saved monthly</div>
            </div>

            {/* Lower Stack Cost Card */}
            <div 
              className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8',
                border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                boxShadow: isDark 
                  ? '0 15px 30px -8px rgba(0, 0, 0, 0.4)' 
                  : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)'
              }}
            >
              <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>90%</div>
              <div className="text-xs" style={{ color: isDark ? '#AAAAAA' : '#555555' }}>lower stack cost</div>
            </div>

            {/* High Conversion Card */}
            <div 
              className="rounded-xl p-4 w-32 transition-all duration-200 hover:scale-105 cursor-pointer"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8',
                border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                boxShadow: isDark 
                  ? '0 15px 30px -8px rgba(0, 0, 0, 0.4)' 
                  : '0 15px 30px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -6px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)'
              }}
            >
              <div className="text-xl lg:text-2xl font-bold mb-0.5" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>78%</div>
              <div className="text-xs" style={{ color: isDark ? '#AAAAAA' : '#555555' }}>higher conversion rates</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorialPlatformSectionAlt;
